import { AppDialog } from "@/components/common";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Building2,
  CalendarCheck2Icon,
  CalendarDays,
  Globe2,
  Layers,
  GitBranch,
} from "lucide-react";
import { useTran } from "@/hooks";
import OrgStatusTag from "@/features/organization/components/OrgStatusTag";

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const formatMonth = (month) => MONTH_NAMES[Number(month) - 1] || "-";

const formatPeriod = (row) => {
  if (!row?.startYear || !row?.startMonth || !row?.endYear || !row?.endMonth) {
    return "-";
  }

  return `${formatMonth(row.startMonth)} ${row.startYear} - ${formatMonth(
    row.endMonth,
  )} ${row.endYear}`;
};

const formatYearMonth = (year, month) => {
  if (!year || !month) return "-";

  return new Date(year, month - 1).toLocaleString("en", {
    month: "long",
    year: "numeric",
  });
};

const getInclusiveMonthCount = (row) => {
  const startYear = Number(row?.startYear);
  const startMonth = Number(row?.startMonth);
  const endYear = Number(row?.endYear);
  const endMonth = Number(row?.endMonth);

  if (
    !Number.isFinite(startYear) ||
    !Number.isFinite(startMonth) ||
    !Number.isFinite(endYear) ||
    !Number.isFinite(endMonth)
  ) {
    return null;
  }

  return (endYear - startYear) * 12 + endMonth - startMonth + 1;
};

const normalizeFramework = (framework, availableFrameworks = []) => {
  const code =
    typeof framework === "string"
      ? framework
      : framework?.code || framework?.value || framework?.id || "";

  if (!code) return null;

  const frameworkDetails = Array.isArray(availableFrameworks)
    ? availableFrameworks.find((item) => item?.code === code)
    : null;

  return {
    code,
    label:
      frameworkDetails?.name ||
      framework?.fullName ||
      frameworkDetails?.fullName ||
      framework?.name ||
      framework?.label ||
      framework?.shortName ||
      frameworkDetails?.label ||
      code,
  };
};

const getOrganizationName = (organization) => {
  if (typeof organization === "string") return organization;
  return organization?.name || organization?.label || organization?.code || "";
};

const getOrganizationId = (organization) => {
  if (typeof organization === "string" || typeof organization === "number") {
    return String(organization);
  }

  const id =
    organization?.id ||
    organization?._id ||
    organization?.value ||
    organization?.key;

  return id == null ? "" : String(id);
};

const getNodeId = (node) => node?.id || node?._id || node?.key || node?.name;

const flattenTreeWithBreadcrumbs = (nodes = [], parentChain = []) => {
  const result = [];

  nodes.forEach((node) => {
    const id = getNodeId(node);
    const label = node?.name || node?.label || node?.title || node?.code || id;
    const current = {
      id: id == null ? "" : String(id),
      label,
      status: node?.status,
      inactiveDate: node?.inactiveDate,
    };
    const chain = [...parentChain, current];

    result.push({ id: current.id, chain });

    if (node.children?.length) {
      result.push(...flattenTreeWithBreadcrumbs(node.children, chain));
    }
  });

  return result;
};

const OrganisationBreadcrumb = ({ chain }) => (
  <div className="rounded-radius border border-border bg-background px-3 py-2">
    <div className="flex min-w-0 flex-wrap items-center gap-x-1 gap-y-1 text-xs leading-5">
      <GitBranch className="h-3 w-3 text-blue-600" />
      {chain.map((item, index) => {
        const isLast = index === chain.length - 1;

        return (
          <span
            key={`${item.id}-${index}`}
            className={
              isLast ? "font-semibold text-blue-600" : "text-muted-foreground"
            }
          >
            {index > 0 ? (
              <span className="px-1 font-normal text-muted-foreground/50">
                {">"}
              </span>
            ) : null}
            <span className="inline-flex items-center gap-1">
              {item.label}
              <OrgStatusTag
                status={item.status}
                inactiveDate={item.inactiveDate}
                className="text-[10px]"
              />
            </span>
          </span>
        );
      })}
    </div>
  </div>
);

const DetailRow = ({ label, value, badge }) => (
  <div className="flex flex-col gap-2 border-b border-border/80 py-3 last:border-b-0">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className="text-left text-sm font-semibold text-foreground">
      {badge ?? value}
    </span>
  </div>
);

const ReportingBoundaryDetailDialog = ({
  open,
  onOpenChange,
  row,
  organizationTree = [],
  availableFrameworks = [],
}) => {
  const { t } = useTran();
  const frameworks = Array.isArray(row?.frameworkCodes)
    ? row.frameworkCodes
        .map((framework) => normalizeFramework(framework, availableFrameworks))
        .filter(Boolean)
    : [];
  const rowOrganizations = Array.isArray(row?.organizations)
    ? row.organizations
    : [];
  const selectedOrganizationIds = new Set(
    rowOrganizations.map(getOrganizationId).filter(Boolean),
  );
  const organizationBreadcrumbs = flattenTreeWithBreadcrumbs(
    organizationTree,
  ).filter((item) => selectedOrganizationIds.has(String(item.id)));
  const fallbackOrganizationBreadcrumbs = rowOrganizations
    .filter(
      (organization) =>
        !organizationBreadcrumbs.some(
          (item) => String(item.id) === getOrganizationId(organization),
        ),
    )
    .map((organization) => {
      const id = getOrganizationId(organization);
      const label = getOrganizationName(organization);

      if (!id || !label) return null;

      return {
        id,
        chain: [{ id, label }],
      };
    })
    .filter(Boolean);
  const displayedOrganizationBreadcrumbs = [
    ...organizationBreadcrumbs,
    ...fallbackOrganizationBreadcrumbs,
  ];
  const isAllOrganization = Boolean(
    row?.isAllOrganization || row?.isAllOranization,
  );
  const durationMonths = getInclusiveMonthCount(row);

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("View Reporting Boundary")}
      description={t(
        "Review reporting year, period, framework, and organization details.",
      )}
      size="lg"
      footer={
        <div className="flex w-full justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("Close")}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 px-1">
        <div className="rounded-xl border border-border bg-card shadow-sm p-5">
          <div className="flex items-center gap-2">
            <CalendarCheck2Icon className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm font-semibold text-foreground">
              {t("Reporting Period Details")}
            </p>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            {t(
              "Review the reporting year, period, and duration of the reporting boundary.",
            )}
          </p>
          <div className="py-2">
            <div className="rounded-radius border border-border px-4 grid grid-cols-2 gap-2 bg-background">
              <DetailRow
                label={t("Reporting Year")}
                value={row?.startYear || "-"}
              />
              <DetailRow
                label={t("Reporting Period")}
                value={formatPeriod(row)}
              />
              <DetailRow
                label={t("Start Year & Month")}
                value={formatYearMonth(row?.startYear, row?.startMonth)}
              />
              <DetailRow
                label={t("End Year & Month")}
                value={formatYearMonth(row?.endYear, row?.endMonth)}
              />
              <DetailRow
                label={t("Duration")}
                value={
                  durationMonths == null
                    ? "-"
                    : `${durationMonths} ${t("months")}`
                }
              />
              <DetailRow
                label={t("Metrics")}
                value={row?.totalDisclosures ?? "-"}
              />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-border p-5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Layers className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm font-semibold text-foreground">
                {t("Frameworks")}
              </p>
            </div>
            {frameworks.length > 0 ? (
              <Badge variant="outline">
                {frameworks.length} {t("selected")}
              </Badge>
            ) : null}
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            {t(
              "Review the frameworks associated with this reporting boundary.",
            )}
          </p>
          {frameworks.length === 0 ? (
            <div className="rounded-md border border-dashed border-border bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
              {t("No frameworks selected")}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {frameworks.map((framework) => (
                <Badge
                  key={framework.code}
                  variant="secondary"
                  className="gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-emerald-700 hover:bg-emerald-50"
                >
                  {/* <span className="rounded bg-emerald-600 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white">
                    {framework.code}
                  </span> */}
                  {framework.label}
                </Badge>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-border p-5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm font-semibold text-foreground">
                {t("Organizations")}
              </p>
            </div>
            {!isAllOrganization &&
            displayedOrganizationBreadcrumbs.length > 0 ? (
              <Badge variant="outline">
                {displayedOrganizationBreadcrumbs.length} {t("selected")}
              </Badge>
            ) : null}
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            {t(
              "Review the organizations associated with this reporting boundary.",
            )}
          </p>
          {isAllOrganization ? (
            <div className="flex items-center gap-2 rounded-md border border-dashed border-border bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
              <Globe2 className="h-4 w-4" />
              {t("All organizations included")}
            </div>
          ) : displayedOrganizationBreadcrumbs.length === 0 ? (
            <div className="rounded-md border border-dashed border-border bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
              {t("No organizations selected")}
            </div>
          ) : (
            <div className="max-h-56 overflow-y-auto rounded-md border border-border bg-muted/10 p-3">
              <div className="space-y-2">
                {displayedOrganizationBreadcrumbs.map((organization) => (
                  <OrganisationBreadcrumb
                    key={organization.chain
                      .map((part) => `${part.id}-${part.label}`)
                      .join(">")}
                    chain={organization.chain}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppDialog>
  );
};

export default ReportingBoundaryDetailDialog;
